import React from 'react'
import { Download, ExternalLink, GraduationCap, Award, Users } from 'lucide-react'
import { personalInfo, education, achievements, volunteerWork } from '../data/portfolio'
import { useScrollAnimation } from '../hooks/useScrollAnimation'

const About: React.FC = () => {
  const { ref: bioRef, isVisible: bioVisible } = useScrollAnimation()
  const { ref: educationRef, isVisible: educationVisible } = useScrollAnimation()
  const { ref: achievementsRef, isVisible: achievementsVisible } = useScrollAnimation()
  const { ref: volunteerRef, isVisible: volunteerVisible } = useScrollAnimation()
  return (
    <section id="about" className="section-padding bg-gray-50 dark:bg-slate-800">
      <div className="container-max">
        <h1 className="text-3xl md:text-4xl font-bold text-center mb-16 text-slate-900 dark:text-white">
          About Me
        </h1>
        
        <div 
          ref={bioRef}
          className={`grid lg:grid-cols-2 gap-12 items-start mb-16 transition-all duration-700 ${
            bioVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Photo and Quick Info */}
          <div className="order-2 lg:order-1 space-y-8">
            {/* Professional Photo */}
            <div className="w-full max-w-md mx-auto aspect-square rounded-2xl overflow-hidden shadow-2xl ring-4 ring-white/20 dark:ring-slate-700/50">
              <img
                src="/images/portrait.jpg"
                alt={`${personalInfo.name} - Professional Portrait`}
                className="w-full h-full object-cover"
              />
            </div>
            
            {/* Quick Stats */}
            <div className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 card-hover">
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick Facts</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Experience:</span>
                  <span className="font-medium text-slate-900 dark:text-white">6+ Years</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Location:</span>
                  <span className="font-medium text-slate-900 dark:text-white">Dubai, UAE</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Specialization:</span>
                  <span className="font-medium text-slate-900 dark:text-white">Backend & AI</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600 dark:text-slate-400">Languages:</span>
                  <span className="font-medium text-slate-900 dark:text-white">English, Arabic</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="order-1 lg:order-2 space-y-8">
            {/* Bio */}
            <div className="prose prose-lg dark:prose-invert max-w-none">
              {personalInfo.bio.split('\n\n').map((paragraph, index) => (
                <p key={index} className="text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
                  {paragraph.trim()}
                </p>
              ))}
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="/resume.pdf" 
                download 
                className="btn-primary inline-flex items-center justify-center space-x-2"
              >
                <Download size={18} />
                <span>Download CV</span>
              </a>
              <a 
                href={personalInfo.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center justify-center space-x-2"
              >
                <ExternalLink size={18} />
                <span>LinkedIn Profile</span>
              </a>
            </div>
          </div>
        </div>

        {/* Education Section */}
        <div 
          ref={educationRef}
          className={`mb-16 transition-all duration-700 delay-200 ${
            educationVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <GraduationCap className="text-blue-600 dark:text-blue-400" size={24} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Education</h3>
          </div>
          
          <div className="bg-white dark:bg-slate-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 card-hover">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
              <div>
                <h4 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
                  {education.degree}
                </h4>
                <p className="text-lg text-blue-600 dark:text-blue-400 font-medium">
                  {education.institution}
                </p>
              </div>
              <div className="text-right mt-4 md:mt-0">
                <p className="text-slate-600 dark:text-slate-400">
                  {education.startYear} - {education.endYear}
                </p>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">
                  GPA: {education.gpa}/{education.maxGpa}
                </p>
              </div>
            </div>
            <p className="text-slate-600 dark:text-slate-300">
              Specialized in Artificial Intelligence with focus on machine learning, computer vision, and intelligent systems development.
            </p>
          </div>
        </div>

        {/* Achievements Section */}
        <div 
          ref={achievementsRef}
          className={`mb-16 transition-all duration-700 delay-300 ${
            achievementsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
              <Award className="text-yellow-600 dark:text-yellow-400" size={24} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Achievements</h3>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            {achievements.map((achievement) => (
              <div 
                key={achievement.id}
                className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 card-hover"
              >
                <div className="flex items-start space-x-4">
                  <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex-shrink-0">
                    <Award className="text-yellow-600 dark:text-yellow-400" size={20} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white mb-2">
                      {achievement.title}
                    </h4>
                    <div className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                      {achievement.description.split('\n').map((line, index) => (
                        <p key={index} className="mb-1">
                          {line.trim()}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Volunteer Work Section */}
        <div 
          ref={volunteerRef}
          className={`transition-all duration-700 delay-500 ${
            volunteerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center space-x-3 mb-8">
            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Users className="text-green-600 dark:text-green-400" size={24} />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 dark:text-white">Volunteer Experience</h3>
          </div>
          
          <div className="space-y-6">
            {volunteerWork.map((work) => (
              <div 
                key={work.id}
                className="bg-white dark:bg-slate-900 p-6 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 card-hover"
              >
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">
                      {work.title}
                    </h4>
                    <p className="text-blue-600 dark:text-blue-400 font-medium">
                      {work.organization}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      {work.location}
                    </p>
                  </div>
                  <div className="text-right mt-2 md:mt-0">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {new Date(work.startDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short' 
                      })} - {new Date(work.endDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short' 
                      })}
                    </p>
                  </div>
                </div>
                <div className="text-slate-600 dark:text-slate-300 leading-relaxed">
                  {work.description.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-2">
                      {paragraph.trim()}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default About